import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { of, from } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { AuthService, AppUser } from 'src/app/services/auth.service';
import { StorageService } from 'src/app/services/storage.service';
import {
  login,
  loginSuccess,
  loginFailure,
  logout,
} from './auth.actions';
import {
  needsSasSignature,
  getReadUrl
} from 'src/app/utils/storage.utils';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private router: Router,
    private storage: StorageService
  ) {}

  /**
   * 🔹 Login con Google (Popup)
   */
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      mergeMap(() =>
        from(this.authService.loginWithPopup()).pipe(
          map((user: AppUser) => {
            localStorage.setItem('user', JSON.stringify(user));
            return loginSuccess({ user });
          }),
          catchError((error) => of(loginFailure({ error })))
        )
      )
    )
  );

  /**
   * 🔹 Inicializar estado de auth (ejecutado al cargar la app)
   * Si el usuario ya estaba logueado en Firebase → loginSuccess
   */
  initAuth$ = createEffect(() =>
    this.authService.onAuthStateChanged$().pipe(
      map((user: AppUser | null) => {
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
          return loginSuccess({ user });
        } else {
          localStorage.removeItem('user');
          return logout();
        }
      }),
      catchError((error) => of(loginFailure({ error })))
    )
  );

  /**
   * 🔹 Logout → limpiar storage y volver a /login
   */
  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        tap(() => {
          localStorage.removeItem('user');
          this.authService.logout();
          this.router.navigate(['/login']);
        })
      ),
    { dispatch: false }
  );

  /**
   * 🔹 Redirigir después del login exitoso
   */
  redirectAfterLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginSuccess),
        tap(() => {
          this.router.navigate(['/tabs/home']);
        })
      ),
    { dispatch: false }
  );

  /**
   * 🔹 Firmar avatar al hacer login (si es necesario)
   */
  signAvatarOnLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginSuccess),
        mergeMap(({ user }) => {
          if (user?.photoURL && needsSasSignature(user.photoURL)) {
            return from(
              this.storage.signUrl(user.photoURL!)
            ).pipe(
              tap((signedUrl) => {
                user.photoURL = getReadUrl(signedUrl);
                localStorage.setItem('user', JSON.stringify(user));
              }),
              catchError((error) => {
                console.error('[Auth] Error firmando avatar', error);
                return of(null);
              })
            );
          }
          return of(null);
        })
      ),
    { dispatch: false }
  );
}
